// Client-side image-dimension validation used by the Wallet Yup schema.
// Apple Wallet rejects passes whose image assets don't match the documented
// pixel grid, so we surface the same constraint up front in the UI:
//   - Icon @1x:  29×29 (exact)
//   - Icon @2x:  58×58 (exact)
//   - Icon @3x:  87×87 (exact)
//   - Logo @1x:  up to 160×50  (max)
//   - Logo @2x:  up to 320×100 (max)
//   - Logo @3x:  up to 480×150 (max)
//
// We probe the picked image client-side with `new Image()` and cache the
// result by URL so re-validating the same field (or switching between fields)
// doesn't refetch. Cache lives at module scope — process-lifetime, never
// invalidated, because uploaded asset URLs are content-addressed.

type Dimensions = { width: number; height: number }

// Probe outcome:
//   - 'ok'      → image loaded and reported usable dimensions
//   - 'unsized' → image loaded but reported 0×0 (typical of an SVG that has no
//                 width/height attrs on its root <svg>); the link is fine, the
//                 file just has no intrinsic pixel size
//   - 'failed'  → onerror fired (404, CORS, bad MIME, unreachable host)
type ProbeResult =
  | { kind: 'ok'; width: number; height: number }
  | { kind: 'unsized' }
  | { kind: 'failed' }

const probeCache = new Map<string, Promise<ProbeResult>>()

// `new Image().src` auto-encodes most things, but Safari has historically
// tripped on raw spaces in filenames (e.g. "gemini-svg (2).svg"). Run the URL
// through encodeURI which is idempotent on already-encoded inputs (it skips
// reserved characters like `:` `/` `?` `#`) so we don't double-encode.
const encodeIfNeeded = (raw: string): string => {
  try {
    return encodeURI(decodeURI(raw))
  } catch {
    return raw
  }
}

const probe = (url: string): Promise<ProbeResult> => {
  const cached = probeCache.get(url)
  if (cached) return cached
  const next = new Promise<ProbeResult>((resolve) => {
    const img = new Image()
    img.onload = () => {
      const width = img.naturalWidth
      const height = img.naturalHeight
      if (width === 0 || height === 0) {
        resolve({ kind: 'unsized' })
      } else {
        resolve({ kind: 'ok', width, height })
      }
    }
    img.onerror = () => resolve({ kind: 'failed' })
    img.src = encodeIfNeeded(url)
  })
  probeCache.set(url, next)
  // Drop transient failures so a follow-up re-pick can retry.
  next.then((result) => {
    if (result.kind === 'failed') probeCache.delete(url)
  })
  return next
}

const isHttpUrl = (value: string): boolean =>
  /^https?:\/\//i.test(value.trim())

const formatLoadError = (): string =>
  'Could not download image — check the URL is reachable and returns a valid image (PNG/JPG)'

const formatUnsizedError = (): string =>
  'Image has no pixel dimensions. SVGs must declare width and height on the <svg> root, or use a PNG.'

// Each checker returns `null` when the image passes, or an error string when
// it doesn't. Returning `null | string` (instead of a discriminated union)
// keeps the call sites tiny — the Yup test just `?? null`-coalesces and uses
// the string directly as the validation message.

export const exactDimensions = async (
  url: string | undefined,
  expected: Dimensions,
): Promise<string | null> => {
  if (!url || !isHttpUrl(url)) return null
  const result = await probe(url)
  if (result.kind === 'failed') return formatLoadError()
  if (result.kind === 'unsized') return formatUnsizedError()
  if (result.width === expected.width && result.height === expected.height) {
    return null
  }
  return `Image must be exactly ${expected.width}×${expected.height} (this file is ${result.width}×${result.height})`
}

export const maxDimensions = async (
  url: string | undefined,
  cap: Dimensions,
): Promise<string | null> => {
  if (!url || !isHttpUrl(url)) return null
  const result = await probe(url)
  if (result.kind === 'failed') return formatLoadError()
  if (result.kind === 'unsized') return formatUnsizedError()
  if (result.width <= cap.width && result.height <= cap.height) {
    return null
  }
  return `Image must be at most ${cap.width}×${cap.height} (this file is ${result.width}×${result.height})`
}

export const IMAGE_DIMENSIONS = {
  icon_url: { kind: 'exact', width: 29, height: 29 },
  icon_2x_url: { kind: 'exact', width: 58, height: 58 },
  icon_3x_url: { kind: 'exact', width: 87, height: 87 },
  logo_url: { kind: 'max', width: 160, height: 50 },
  logo_2x_url: { kind: 'max', width: 320, height: 100 },
  logo_3x_url: { kind: 'max', width: 480, height: 150 },
} as const

export type WalletImageField = keyof typeof IMAGE_DIMENSIONS
